import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { generateUniqueId } from "../../utility/layoutTree";

let rowIdCounter = 0;
const nextRowId = () => `row-${rowIdCounter++}`;

// Flattens a layout tree into one row per site — leaves sharing the same
// parent path are grouped back into a single row's services[]. Used once to
// seed the editor when opening an existing advanced client.
function rowsFromTree(nodes, prefix = []) {
  const rows = [];
  const rowByPath = new Map();

  const walk = (list, path) => {
    (list || []).forEach((node) => {
      if (node.children && node.children.length > 0) {
        walk(node.children, [...path, node.title]);
      } else {
        const sitePath = path.join(" / ");
        let row = rowByPath.get(sitePath);
        if (!row) {
          row = { rowId: nextRowId(), sitePath, services: [] };
          rowByPath.set(sitePath, row);
          rows.push(row);
        }
        row.services.push(node.title);
      }
    });
  };
  walk(nodes, prefix);
  return rows;
}

// Builds a nested layout tree from the flat rows — rows sharing a site path
// are grouped together, and each selected service becomes its own leaf.
function treeFromRows(rows) {
  const root = [];

  const findOrCreateGroup = (siblings, title) => {
    const existing = siblings.find((n) => n.title === title && n.children);
    if (existing) return existing;
    const id = generateUniqueId(title, siblings);
    const group = { id, title, type: "group", children: [] };
    siblings.push(group);
    return group;
  };

  rows.forEach(({ sitePath, services }) => {
    const segments = (sitePath || "")
      .split("/")
      .map((s) => s.trim())
      .filter(Boolean);
    let siblings = root;
    segments.forEach((seg) => {
      siblings = findOrCreateGroup(siblings, seg).children;
    });
    (services || []).forEach((service) => {
      const alreadyExists = siblings.some((n) => n.title === service && !n.children);
      if (!alreadyExists) {
        const id = generateUniqueId(service, siblings);
        siblings.push({ id, title: service, type: "service" });
      }
    });
  });

  return root;
}

const fieldSx = {
  bgcolor: "#343a40",
  input: { color: "#fff" },
  "& .MuiOutlinedInput-root fieldset": { borderColor: "#6c757d" },
};

// Dark dropdown panel (matches the dialog) with compact rows instead of
// MUI's default light, tall-spaced menu.
const servicesMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "#343a40",
      maxHeight: 320,
      "& .MuiMenuItem-root": {
        color: "#fff",
        minHeight: "auto",
        py: 0.5,
      },
      "& .MuiMenuItem-root:hover": {
        bgcolor: "#495057",
      },
      "& .MuiMenuItem-root.Mui-selected": {
        bgcolor: "transparent",
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        bgcolor: "#495057",
      },
    },
  },
};

// Flat, row-based layout editor — one row per site. The "Site" column is free
// text (site/plant/floor names are client-specific and can't be predicted),
// but "Services" is a checkbox multi-select built from the same canonical
// service list used for simple clients — no free typing, so it can't drift
// into typos or casing variants like "Snacks" vs "SNACKS".
export default function LayoutRowsEditor({ nodes, onChange, serviceOptions }) {
  const [rows, setRows] = useState(() => {
    const seeded = rowsFromTree(nodes);
    return seeded.length > 0 ? seeded : [{ rowId: nextRowId(), sitePath: "", services: [] }];
  });

  const emit = (next) => {
    setRows(next);
    onChange(treeFromRows(next));
  };

  const updateRow = (rowId, field, value) => {
    emit(rows.map((r) => (r.rowId === rowId ? { ...r, [field]: value } : r)));
  };

  const addRow = () => {
    emit([...rows, { rowId: nextRowId(), sitePath: "", services: [] }]);
  };

  const removeRow = (rowId) => {
    const next = rows.filter((r) => r.rowId !== rowId);
    emit(next.length > 0 ? next : [{ rowId: nextRowId(), sitePath: "", services: [] }]);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="caption" sx={{ color: "#adb5bd" }}>
        One row per site. Leave "Site" blank if it isn't under a site; use "/" for a
        site with sub-areas, e.g. "AKDR 7th Floor / Day". Check every service offered
        there.
      </Typography>

      {rows.map((row) => (
        <Box key={row.rowId} sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Site (optional), e.g. Plant 1"
            value={row.sitePath}
            onChange={(e) => updateRow(row.rowId, "sitePath", e.target.value)}
            sx={{ flex: "1 1 160px", minWidth: 140, ...fieldSx }}
          />
          <Select
            multiple
            size="small"
            displayEmpty
            value={row.services}
            onChange={(e) => updateRow(row.rowId, "services", e.target.value)}
            input={<OutlinedInput />}
            MenuProps={servicesMenuProps}
            renderValue={(selected) => (
              <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#fff",
                }}
              >
                {selected.length > 0 ? selected.join(", ") : "Select services..."}
              </Box>
            )}
            sx={{ flex: "0 0 240px", width: 240, ...fieldSx, "& .MuiSelect-select": { color: "#fff" } }}
          >
            {serviceOptions.map((option) => (
              <MenuItem key={option} value={option} dense>
                <Checkbox checked={row.services.includes(option)} size="small" sx={{ p: 0.5 }} />
                <Typography sx={{ fontSize: 14 }}>{option}</Typography>
              </MenuItem>
            ))}
          </Select>
          <IconButton size="small" onClick={() => removeRow(row.rowId)} sx={{ color: "#f44336" }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addRow}
        sx={{ alignSelf: "flex-start", color: "#90caf9" }}
      >
        Add Row
      </Button>
    </Box>
  );
}
